/**
 * The Settings tab's form, pure: what each switch shows before the vendor
 * touched it (the defaults the API applied), what changing a store does to
 * its pin, and whether anything differs from what was loaded.
 */
import { NextStepSettings, RequestKind, ResolvedNextSteps, VendorStore, VisitSlot } from './types';

/** Visit defaults, as the API applies them (next-steps.ts DEFAULT_VISIT). */
export const DEFAULT_VISIT = { days: [1, 2, 3, 4, 5, 6], slots: ['morning', 'afternoon'] as VisitSlot[], leadDays: 1, horizonDays: 21 };

/** The weekday toggles' order, Monday first (0 = Sunday, as the API counts). */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

/** An action's switch: what the vendor set, else whether shoppers currently see it. */
export function actionEnabled(nextSteps: NextStepSettings, resolved: ResolvedNextSteps | null | undefined, kind: RequestKind): boolean {
    const set = nextSteps?.[kind]?.enabled;
    if (typeof set === 'boolean') return set;
    return !!resolved?.actions?.includes(kind);
}

/** The master switch: what the vendor set, else off (next steps are opt-in). */
export function nextStepsEnabled(nextSteps: NextStepSettings, resolved: ResolvedNextSteps | null | undefined): boolean {
    if (typeof nextSteps?.enabled === 'boolean') return nextSteps.enabled;
    return resolved?.enabled ?? false;
}

/** The visit settings to show: what the vendor set over the defaults. */
export function visitSettings(nextSteps: NextStepSettings, resolved?: ResolvedNextSteps | null) {
    const base = resolved?.visit ?? DEFAULT_VISIT;
    const v = nextSteps?.visit ?? {};
    return {
        days: v.days ?? base.days,
        slots: v.slots ?? base.slots,
        leadDays: v.leadDays ?? base.leadDays,
        horizonDays: v.horizonDays ?? base.horizonDays,
    };
}

/** Change one part of one action, keeping the rest. */
export function patchAction<K extends RequestKind>(nextSteps: NextStepSettings, kind: K, patch: NonNullable<NextStepSettings[K]>): NextStepSettings {
    return { ...nextSteps, [kind]: { ...(nextSteps[kind] ?? {}), ...patch } };
}

/** A value toggled in or out of a list, kept in `order`. */
export function toggleIn<T>(list: T[], value: T, order: readonly T[]): T[] {
    const has = list.includes(value);
    const next = has ? list.filter((v) => v !== value) : [...list, value];
    return order.filter((v) => next.includes(v));
}

/** Clamp a typed number into a range; empty or junk falls back. */
export function clampInt(value: string | number, min: number, max: number, fallback: number): number {
    const n = Math.round(Number(value));
    if (value === '' || !Number.isFinite(n)) return fallback;
    return Math.min(max, Math.max(min, n));
}

// ── Stores ──────────────────────────────────────────────────────────────

const ADDRESS_FIELDS: Array<keyof VendorStore> = ['address', 'city', 'postcode', 'country'];

/** A fresh, unsaved store. The id only has to be unique in the list; the API keeps it. */
export function newStore(existing: VendorStore[], name = ''): VendorStore {
    const ids = new Set(existing.map((s) => s.id));
    let n = existing.length + 1;
    while (ids.has(`store-${n}`)) n++;
    return { id: `store-${n}`, name, sampleDesk: true };
}

/** Pinned on the map: the nearest-store sort can use it. */
export const storeLocated = (s: VendorStore): boolean => typeof s.lat === 'number' && typeof s.lng === 'number';

/**
 * Edit one field of a store. Moving the address drops the old pin, so the
 * API locates the store again on save instead of keeping it where it was.
 */
export function editStore(store: VendorStore, field: keyof VendorStore, value: any): VendorStore {
    const next: VendorStore = { ...store, [field]: value };
    if (ADDRESS_FIELDS.includes(field) && (store[field] ?? '') !== (value ?? '')) {
        delete next.lat;
        delete next.lng;
    }
    return next;
}

/** The stores as the API takes them: trimmed, empty fields dropped, unnamed rows left out. */
export function storesForSave(stores: VendorStore[]): VendorStore[] {
    return stores
        .map((s) => {
            const out: any = {};
            for (const [k, v] of Object.entries(s)) {
                if (typeof v === 'string') {
                    const trimmed = k === 'country' ? v.trim().toUpperCase() : v.trim();
                    if (trimmed) out[k] = trimmed;
                } else if (v !== undefined && v !== null) {
                    out[k] = v;
                }
            }
            return out as VendorStore;
        })
        .filter((s) => !!s.name);
}

/** Stores the form cannot save: a row with details but no name. */
export function unnamedStores(stores: VendorStore[]): number {
    return stores.filter((s) => !(s.name || '').trim() && ADDRESS_FIELDS.concat(['phone', 'email', 'hours', 'bookingUrl']).some((f) => String(s[f] ?? '').trim())).length;
}

/** Order-independent JSON for dirty checks. */
export function stableJson(value: unknown): string {
    const sort = (v: any): any => {
        if (Array.isArray(v)) return v.map(sort);
        if (v && typeof v === 'object') {
            return Object.keys(v)
                .sort()
                .reduce((o: any, k) => {
                    if (v[k] !== undefined) o[k] = sort(v[k]);
                    return o;
                }, {});
        }
        return v;
    };
    return JSON.stringify(sort(value));
}
